import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';
import { NotificationService } from '../../../services/notification.service';
import { SettingsService } from '../../../services/settings.service';
import { LayoutService } from '../../../shared/components/app-layout/shared-layout/layout.service';
import { AppState } from '../../../store';
import { StoreItemActions } from '../../../store/entities/store-item/store-item.actions';
import { StoreItemEntity } from '../../../store/entities/store-item/store-item.model';
import * as AOS from 'aos';
import { AuthService } from '../../../services/auth.service';
import { StorageService } from '../../../services/storage.service';
import { StoriesDataService, Story } from '../../../services/stories-data.service';
import { Observable, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-premium-stories',
  templateUrl: './premium-stories.component.html',
  styleUrl: './premium-stories.component.scss'
})
export class PremiumStoriesComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  private readonly layoutService = inject(LayoutService);
  private readonly storiesDataService = inject(StoriesDataService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);

  title = 'Publication Management';

  stories: Story[] = [];
  filteredStories: Story[] = [];
  loading = false;
  searchQuery = '';

  currentIndex = 0;
  visibleCount = 3;
  slideWidth = 100 / 3;
  isHovering = false;

  private autoPlayInterval?: number;
  private resizeHandler = () => this.updateVisibleCount();

  ngOnInit() {
    this.loadStories();
    this.updateVisibleCount();
    this.startAutoPlay();
  }

  loadStories() {
    this.loading = true;

    this.storiesDataService
      .getStories({ itemsPerPage: 10 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stories) => {
          this.stories = stories;
          this.filteredStories = [...stories];
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  ngAfterViewInit() {
    AOS.init({
      duration: 800,
      easing: 'ease-in-out',
      once: true
    });

    window.addEventListener('resize', this.resizeHandler);
  }

  ngOnDestroy() {
    this.stopAutoPlay();
    window.removeEventListener('resize', this.resizeHandler);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------- SEARCH & FILTER ----------------

  onSearchChange() {
    const query = this.searchQuery.toLowerCase().trim();

    if (!query) {
      this.filteredStories = [...this.stories];
    } else {
      this.filteredStories = this.stories.filter(story =>
        story.title.toLowerCase().includes(query) ||
        story.subtitle?.toLowerCase().includes(query) ||
        story.author?.toLowerCase().includes(query) ||
        story.category?.toLowerCase().includes(query)
      );
    }

    // Reset carousel to first slide after search
    this.currentIndex = 0;
    this.cdr.markForCheck();
  }

  clearSearch() {
    this.searchQuery = '';
    this.onSearchChange();
  }

  seeAllStories() {
    // Option 1: Navigate to dedicated all stories page
    this.router.navigate(['/landing/stories']);

    
  }

  // ---------------- CAROUSEL ----------------

  nextSlide() {
    if (this.canNavigateNext) {
      this.currentIndex++;
    }
  }

  previousSlide() {
    if (this.canNavigatePrev) {
      this.currentIndex--;
    }
  }

  startAutoPlay() {
    this.stopAutoPlay();
    this.autoPlayInterval = window.setInterval(() => {
      if (!this.isHovering && this.filteredStories.length > this.visibleCount) {
        this.currentIndex =
          this.currentIndex >= this.filteredStories.length - this.visibleCount
            ? 0
            : this.currentIndex + 1;
      }
    }, 5000);
  }

  stopAutoPlay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = undefined;
    }
  }

  onCarouselHover(isHovering: boolean) {
    this.isHovering = isHovering;
    isHovering ? this.stopAutoPlay() : this.startAutoPlay();
  }

  updateVisibleCount() {
    const width = window.innerWidth;

    if (width < 640) {
      this.visibleCount = 1;
      this.slideWidth = 100;
    } else if (width < 1024) {
      this.visibleCount = 2;
      this.slideWidth = 50;
    } else {
      this.visibleCount = 3;
      this.slideWidth = 100 / 3;
    }

    this.cdr.markForCheck();
  }

  // ---------------- UI HELPERS ----------------

  get showNavigationArrows(): boolean {
    return this.filteredStories.length > this.visibleCount;
  }

  get canNavigateNext(): boolean {
    return this.currentIndex < this.filteredStories.length - this.visibleCount;
  }

  get canNavigatePrev(): boolean {
    return this.currentIndex > 0;
  }

  trackByStoryId(_: number, story: Story): number {
    return story.id;
  }

  onImageError(event: Event) {
    (event.target as HTMLImageElement).src =
      'assets/images/default-magazine.jpg';
  }

  openStory(story: Story) {
    this.router.navigate(['/story', story.uniqueId]);
  }

  downloadStory(story: Story) {
    // download logic
  }

  closePanel() {
    this.layoutService.closePanel();
  }
}